/**
 * MOB search-area layer — the RNLI Rapid Response search picture on the chart:
 * the datum the casualty has drifted to, the search circle (radius = drift error
 * + fix error) around it, the square search box that encloses that circle, and,
 * when a ground-based pattern is chosen, the plotted parallel-track or creeping-
 * line search with its commence-search position.
 *
 * Unlike the sector search and expanding square — which are steered through the
 * water on a heading and a stopwatch and so are deliberately NOT drawn — the area
 * patterns are run over the ground, so they belong on the chart as track lines.
 *
 * Kept magenta to sit with the MOB mark it grows out of; the plotted search track
 * takes the UI accent so the legs read clearly against the circle and box.
 */
import type { Map as MaplibreMap } from 'maplibre-gl';
import type { Feature, FeatureCollection } from 'geojson';
import { swingCircle } from '@/safety';
import { getPalette } from './style/palettes';
import type { Palette } from '@/store';
import type { MobSearchOverlay } from '@/store/mobSearch';

const SOURCE_ID = 'mob-search-area';

function build(overlay: MobSearchOverlay | null): FeatureCollection {
  const features: Feature[] = [];
  if (overlay) {
    // Search circle: a geodesic ring around the datum, radius = search radius.
    features.push({
      type: 'Feature',
      geometry: { type: 'Polygon', coordinates: [swingCircle(overlay.datum, overlay.radiusNm)] },
      properties: { kind: 'search-circle' },
    });
    // Search box: the closed A→B→C→D→A ring.
    features.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: overlay.boxRing },
      properties: { kind: 'search-box' },
    });
    // Lettered box corners.
    for (const label of ['A', 'B', 'C', 'D'] as const) {
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: overlay.boxCorners[label] },
        properties: { kind: 'box-corner', label },
      });
    }
    // Datum the circle and box are built on.
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: overlay.datum },
      properties: { kind: 'search-datum' },
    });
    // Ground-based search track + commence-search position, when present.
    if (overlay.area) {
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: overlay.area.vertices },
        properties: { kind: 'search-track' },
      });
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: overlay.area.csp },
        properties: { kind: 'search-csp', label: 'CSP' },
      });
    }
  }
  return { type: 'FeatureCollection', features };
}

export function ensureMobSearchAreaLayer(
  map: MaplibreMap,
  overlay: MobSearchOverlay | null,
  palette: Palette,
): void {
  const p = getPalette(palette);
  if (!map.getSource(SOURCE_ID)) {
    map.addSource(SOURCE_ID, { type: 'geojson', data: build(overlay) });
  } else {
    (map.getSource(SOURCE_ID) as maplibregl.GeoJSONSource).setData(build(overlay));
  }

  if (!map.getLayer('mob-search-circle-fill')) {
    map.addLayer({
      id: 'mob-search-circle-fill',
      type: 'fill',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'search-circle'],
      paint: { 'fill-color': p.CHMGD, 'fill-opacity': 0.06 },
    });
  }
  if (!map.getLayer('mob-search-circle-line')) {
    map.addLayer({
      id: 'mob-search-circle-line',
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'search-circle'],
      paint: { 'line-color': p.CHMGD, 'line-width': 1.2, 'line-dasharray': [2, 2] },
    });
  }
  if (!map.getLayer('mob-search-box-line')) {
    map.addLayer({
      id: 'mob-search-box-line',
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'search-box'],
      paint: { 'line-color': p.CHMGD, 'line-width': 1.4, 'line-dasharray': [5, 3] },
    });
  }
  if (!map.getLayer('mob-search-track-line')) {
    map.addLayer({
      id: 'mob-search-track-line',
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'search-track'],
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: { 'line-color': p.UI_ACCENT, 'line-width': 2 },
    });
  }
  if (!map.getLayer('mob-search-datum')) {
    map.addLayer({
      id: 'mob-search-datum',
      type: 'circle',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'search-datum'],
      paint: {
        'circle-radius': 4,
        'circle-color': p.CHMGD,
        'circle-stroke-color': p.UI_BG,
        'circle-stroke-width': 1.5,
      },
    });
  }
  if (!map.getLayer('mob-search-csp')) {
    map.addLayer({
      id: 'mob-search-csp',
      type: 'circle',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'search-csp'],
      paint: {
        'circle-radius': 4,
        'circle-color': p.UI_ACCENT,
        'circle-stroke-color': p.UI_BG,
        'circle-stroke-width': 1.5,
      },
    });
  }
  if (!map.getLayer('mob-search-corner-labels')) {
    map.addLayer({
      id: 'mob-search-corner-labels',
      type: 'symbol',
      source: SOURCE_ID,
      filter: ['any', ['==', ['get', 'kind'], 'box-corner'], ['==', ['get', 'kind'], 'search-csp']],
      layout: {
        'text-field': ['get', 'label'],
        'text-font': ['Open Sans Regular'],
        'text-size': 12,
        'text-offset': [0, -1],
        'text-anchor': 'bottom',
        'text-allow-overlap': true,
      },
      paint: { 'text-color': p.CHMGD, 'text-halo-color': p.UI_BG, 'text-halo-width': 1.6 },
    });
  }
}

export function updateMobSearchAreaLayer(map: MaplibreMap, overlay: MobSearchOverlay | null): void {
  const src = map.getSource(SOURCE_ID);
  if (src) (src as maplibregl.GeoJSONSource).setData(build(overlay));
}
